import { useCallback, useEffect, useState } from 'react';
import memoizeOne from 'memoize-one';
import { SOFTWARE } from '@jira/platform__application-editions/src/application-key.tsx';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import getExplicitlyLicensedProducts from '@atlassian/jira-common-get-explicitly-licensed-products/src/index.tsx';
import { getProductKey } from '@atlassian/jira-external-urls/src/utils/get-product-key/index.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useSubProduct } from '@atlassian/jira-subproduct/src/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { useCanProductUserUpgradeAndPayQuery } from './can-product-user-upgrade-and-pay/useCanProductUserUpgradeAndPayQuery.tsx';

const fetchEntitlementDetails = (entitlementId: string) =>
	performGetRequest(`/gateway/api/ccp/api/v1/entitlements/${entitlementId}/details`, {
		headers: {
			'Content-Type': 'application/json',
		},
	});

const fetchBillingAdmins = (transactionAccountId: string) =>
	performGetRequest('/gateway/api/ccp/api/v1/billing-admins', {
		headers: {
			'Content-Type': 'application/json',
			'X-transaction-account': transactionAccountId,
		},
	});

export const memoizedFetchEntitlementDetails = memoizeOne(fetchEntitlementDetails);
export const memoizedFetchBillingAdmins = memoizeOne(fetchBillingAdmins);

type UseCanUpgradeAndPayForJswFreeParams = {
	/** The name of the component or package from which this hook is called */
	caller: string;
	/** If true, the check is not performed. This is useful for when you want the check to execute conditionally */
	skip?: boolean;
};

/**
 * This hook determines whether the current user has permission to upgrade and manage billing for the Jira Software Free instance.
 *
 * @deprecated We should use the AGG query to fetch this information
 */
export const useCanUpgradeAndPayForJswFree = ({
	caller,
	skip = false,
}: UseCanUpgradeAndPayForJswFreeParams) => {
	const [canUpgradeAndPay, setCanUpgradeAndPay] = useState<boolean | undefined>(undefined);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<Error | undefined>(undefined);
	const [ineligibilityReason, setIneligibilityReason] = useState<string | undefined>(undefined);

	const { isSiteAdmin, atlassianAccountId, productEntitlementDetails } = useTenantContext();

	const isBillingSystemCcp = productEntitlementDetails?.[SOFTWARE]?.billingSourceSystem === 'CCP';
	const entitlementId = productEntitlementDetails?.[SOFTWARE]?.entitlementId;

	const checkIsCcpBillingAdmin = useCallback(async (): Promise<boolean | undefined> => {
		if (!entitlementId) {
			throw new Error('Empty entitlementId');
		}

		if (!atlassianAccountId) {
			throw new Error('Empty atlassianAccountId');
		}

		const entitlementDetails = await memoizedFetchEntitlementDetails(entitlementId);
		const transactionAccountId = entitlementDetails?.transactionAccountId;
		const billingAdminsResponse = await memoizedFetchBillingAdmins(transactionAccountId);
		const isBillingAdmin = !!billingAdminsResponse?.data?.find(
			({ id = null }) => id === atlassianAccountId,
		);

		return isBillingAdmin;
	}, [atlassianAccountId, entitlementId]);

	/**
	 * This `useEffect` performs the permission checks.
	 */
	useEffect(() => {
		if (skip) {
			return;
		}

		setLoading(true);

		// Only logged in site admins can upgrade
		if (isSiteAdmin) {
			if (isBillingSystemCcp) {
				// On CCP, only billing admins can manage billing
				checkIsCcpBillingAdmin()
					.then((isBillingAdmin) => {
						setCanUpgradeAndPay(isBillingAdmin);
						// Empty ineligibilityReason marks that the requisites were checked
						setIneligibilityReason(isBillingAdmin ? '' : 'not a billing admin');
					})
					.catch((e) => {
						setError(e);
						setIneligibilityReason('error checking eligibility');
					})
					.finally(() => {
						setLoading(false);
					});
			} else {
				// On HAMS, site admins can also manage billing
				setCanUpgradeAndPay(true);
				setLoading(false);
				// Empty ineligibilityReason marks that the requisites were checked
				setIneligibilityReason('');
			}
		} else {
			setCanUpgradeAndPay(false);
			setLoading(false);
			setIneligibilityReason('not a site admin');
		}
	}, [atlassianAccountId, caller, checkIsCcpBillingAdmin, isBillingSystemCcp, isSiteAdmin, skip]);

	useEffect(() => {
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
		if (error) {
			fireErrorAnalytics({
				error,
				meta: {
					id: 'useCanUpgradeAndPayForJswFree',
					packageName: 'jiraBilling',
					teamName: 'growth-tako',
				},
				attributes: {
					billingSourceSystem: isBillingSystemCcp ? 'CCP' : 'HAMS',
					caller,
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	}, [error, isBillingSystemCcp, caller]);

	return { canUpgradeAndPay, loading, error, ineligibilityReason };
};

type UseCanUpgradeAndPayForCurrentProductParams = {
	/** The name of the component or package from which this hook is called */
	caller: string;
	/** If true, the check is not performed. This is useful for when you want the check to execute conditionally */
	skip?: boolean;
};

/**
 * This hook determines whether the current user has permission to upgrade and manage billing for the product they are currently in,
 * e.g. Jira Software, Jira Service Management, Jira Work Management, etc.
 * The hook will fail and return an error if executed outside the context of a specific product (e.g. on the "Your work" page).
 */
export const useCanUpgradeAndPayForCurrentProduct = ({
	caller,
	skip = false,
}: UseCanUpgradeAndPayForCurrentProductParams) => {
	const [canUpgradeAndPay, setCanUpgradeAndPay] = useState<boolean | undefined>(undefined);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<Error | undefined>(undefined);

	const tenantContext = useTenantContext();
	const subProduct = useSubProduct();
	const explicitlyLicensedProducts = getExplicitlyLicensedProducts(tenantContext);
	const applicationKey = getProductKey(subProduct, explicitlyLicensedProducts);

	useEffect(() => {
		if (skip) {
			return;
		}

		if (!applicationKey) {
			setError(new Error('Empty applicationKey'));
			return;
		}

		const isBillingSystemCcp =
			tenantContext.productEntitlementDetails?.[applicationKey]?.billingSourceSystem === 'CCP';
		const entitlementId = tenantContext.productEntitlementDetails?.[applicationKey]?.entitlementId;

		const checkIsCcpBillingAdmin = async (): Promise<boolean | undefined> => {
			if (!entitlementId) {
				throw new Error('Empty entitlementId');
			}

			if (!tenantContext.atlassianAccountId) {
				throw new Error('Empty atlassianAccountId');
			}

			const entitlementDetails = await memoizedFetchEntitlementDetails(entitlementId);
			const transactionAccountId = entitlementDetails?.transactionAccountId;
			const billingAdminsResponse = await memoizedFetchBillingAdmins(transactionAccountId);
			const isBillingAdmin =
				billingAdminsResponse?.data?.some(
					({ id = null }) => id === tenantContext.atlassianAccountId,
				) ?? false;

			return isBillingAdmin;
		};

		setLoading(true);

		// Only logged in site admins can upgrade
		if (tenantContext.isSiteAdmin && tenantContext.atlassianAccountId) {
			if (isBillingSystemCcp) {
				// On CCP, only billing admins can manage billing
				checkIsCcpBillingAdmin()
					.then((isBillingAdmin) => {
						setCanUpgradeAndPay(isBillingAdmin);
					})
					.catch((e) => {
						setError(e);
						fireErrorAnalytics({
							error: e,
							meta: {
								id: 'useCanUpgradeAndPayForCurrentProduct',
								packageName: 'jiraBilling',
								teamName: 'growth-tako',
							},
							attributes: {
								billingSourceSystem: isBillingSystemCcp ? 'CCP' : 'HAMS',
								caller,
								subProduct,
								applicationKey,
								licensedProducts: tenantContext.licensedProducts,
								productEntitlementDetails: tenantContext.productEntitlementDetails,
							},
							sendToPrivacyUnsafeSplunk: true,
						});
					})
					.finally(() => {
						setLoading(false);
					});
			} else {
				// On HAMS, site admins can also manage billing
				setCanUpgradeAndPay(true);
				setLoading(false);
			}
		} else {
			setCanUpgradeAndPay(false);
			setLoading(false);
		}
	}, [
		applicationKey,
		caller,
		skip,
		subProduct,
		tenantContext.atlassianAccountId,
		tenantContext.isSiteAdmin,
		tenantContext.licensedProducts,
		tenantContext.productEntitlementDetails,
	]);

	return { canUpgradeAndPay, loading, error };
};

type UseCanProductUserUpgradeAndPayProps = {
	skip: boolean;
	productKey?: 'jira-software.ondemand' | 'jira-servicedesk.ondemand' | 'jira-product-discovery';
};

/**
 * Helper function to determine whether a product user can upgrade and pay.
 */
export function useCanProductUserUpgradeAndPay({
	skip,
	productKey,
}: UseCanProductUserUpgradeAndPayProps) {
	const {
		data,
		isLoading,
		error,
		fetch: fetchCanProductUserUpgradeAndPay,
	} = useCanProductUserUpgradeAndPayQuery();

	useEffect(() => {
		if (skip || !productKey) {
			return;
		}

		fetchCanProductUserUpgradeAndPay(productKey);
	}, [fetchCanProductUserUpgradeAndPay, productKey, skip]);

	const canProductUserUpgradeAndPay = data?.canProductUserUpgradeAndPay;
	const isProductUserBillingAdmin = data?.isBillingAdmin;
	const hasPaymentDetailsOnFile = data?.hasPaymentDetailsOnFile;

	return {
		isProductUserBillingAdmin,
		canProductUserUpgradeAndPay,
		hasPaymentDetailsOnFile,
		isLoading,
		error,
	};
}
